import { useId, useRef } from "react";

/**
 * A segmented control for the canvas.
 *
 * Pricing's Monthly/Annual switch was built inline and is the only one of its
 * kind on the site, so every future instance would have been built inline too.
 *
 * It is a radiogroup, not a row of buttons, and that is the whole reason this
 * file exists. A group of buttons announces as "button, Monthly. button,
 * Annual." with no indication that they are alternatives, that one is chosen,
 * or how many there are. A radiogroup announces "Monthly, radio button 1 of 2,
 * selected" — which is what the control actually is.
 *
 * That choice carries a keyboard obligation: a radiogroup is ONE tab stop, and
 * the arrow keys move between options. Tab landing on every segment is the
 * usual mistake and it makes a two-option switch cost two tabs to skip.
 */

const SANS = "'Manrope', sans-serif";

export interface Segment<T extends string> {
  value: T;
  label: string;
  /** Optional note, e.g. a discount on the annual option. */
  hint?: string;
}

export interface SegmentedControlProps<T extends string> {
  segments: ReadonlyArray<Segment<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Accessible name for the group — what is being switched. */
  label: string;
}

export default function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  label,
}: SegmentedControlProps<T>) {
  const groupId = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const index = segments.findIndex((s) => s.value === value);

  /**
   * Arrow keys move and select in one step, which is the expected behaviour
   * for a radiogroup whose options are cheap to apply. Focus follows, so the
   * keyboard reader hears the selection rather than only seeing it.
   */
  const onKeyDown = (e: React.KeyboardEvent) => {
    const last = segments.length - 1;
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = index >= last ? 0 : index + 1;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = index <= 0 ? last : index - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    if (next === null) return;
    e.preventDefault();
    onChange(segments[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: 4,
        borderRadius: 999,
        border: "1px solid rgba(var(--ink-rgb),.16)",
        background: "var(--paper2)",
      }}
    >
      {segments.map((seg, i) => {
        const selected = seg.value === value;
        return (
          <button
            key={seg.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            id={`${groupId}-${seg.value}`}
            // One tab stop for the group: only the selected option is
            // reachable by Tab, and the arrows move within.
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(seg.value)}
            className="dc-seg"
            style={{
              display: "inline-flex",
              alignItems: "baseline",
              gap: 7,
              border: "none",
              borderRadius: 999,
              padding: "9px 18px",
              cursor: selected ? "default" : "pointer",
              fontFamily: SANS,
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              transition: "background .22s, color .22s",
              // --chip, not --btn. They are the same value in light and differ
              // in dark (#1E222A panel vs #F2F4F6 pill), and both segments this
              // replaces were on chip — so using btn here would have been a
              // silent dark-mode restyle riding along with an a11y fix.
              background: selected ? "var(--chip)" : "transparent",
              color: selected ? "var(--chipfg)" : "rgba(var(--ink-rgb),.7)",
            }}
          >
            {seg.label}
            {seg.hint && (
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 500,
                  letterSpacing: ".04em",
                  // Inherits on the selected pill so it cannot fall to an ink
                  // token that the pill's own background has already covered.
                  color: selected ? "inherit" : "var(--link)",
                  opacity: selected ? 0.75 : 1,
                }}
              >
                {seg.hint}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
